/**
 * DisclosureOS — Frontend API Service Layer
 * Shared infrastructure for all frontend modules:
 *   - apiFetch(): authenticated fetch wrapper
 *   - useFetch(): declarative data loading hook
 *   - useMutation(): POST/PUT/DELETE with loading states
 *   - useGraphWS(): WebSocket hook for real-time cascade events
 *   - useModuleNav(): cross-module navigation
 *   - Loading/Error/Empty/WSIndicator UI components
 */
import { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api/v1";
const AUTH_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ═══════════════════════════════════════════════════════════════
// AUTH CONTEXT — login/logout/user state via localStorage
// ═══════════════════════════════════════════════════════════════
const AuthContext = createContext(null);

export function clearSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

// Validates a stored token against /auth/me.
// Returns { valid, user } — network/server errors keep the session (valid: true, user: null).
export async function validateSession(token) {
  try {
    const resp = await fetch(`${AUTH_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (resp.status === 401 || resp.status === 403) return { valid: false, user: null };
    if (!resp.ok) return { valid: true, user: null };
    return { valid: true, user: await resp.json() };
  } catch {
    return { valid: true, user: null };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      validateSession(token).then(({ valid, user: fresh }) => {
        if (!valid) {
          clearSession();
          setUser(null);
        } else if (fresh) {
          localStorage.setItem("user", JSON.stringify(fresh));
          setUser(fresh);
        } else {
          try { setUser(JSON.parse(savedUser)); } catch {}
        }
        setLoading(false);
      });
      return;
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);
    const resp = await fetch(`${AUTH_BASE}/auth/login`, { method: "POST", body: formData });
    if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(d.detail || "Login failed"); }
    const data = await resp.json();
    localStorage.setItem("token", data.access_token);
    const meResp = await fetch(`${AUTH_BASE}/auth/me`, { headers: { Authorization: `Bearer ${data.access_token}` } });
    if (!meResp.ok) throw new Error("Failed to fetch user profile");
    const userData = await meResp.json();
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const loginSSO = useCallback(async (token) => {
    localStorage.setItem("token", token);
    const meResp = await fetch(`${AUTH_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (!meResp.ok) throw new Error("SSO token invalid");
    const userData = await meResp.json();
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, loginSSO, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Fallback for components not wrapped in AuthProvider
    return { user: null, login: async () => {}, loginSSO: async () => {}, logout: () => {}, loading: false };
  }
  return ctx;
}

// ═══════════════════════════════════════════════════════════════
// AUTHENTICATED FETCH WRAPPER
// ═══════════════════════════════════════════════════════════════
export async function apiFetch(path, options = {}) {
  // Path shapes: full URL (passthrough), origin-absolute "/api/..." (legacy
  // non-v1 endpoints — don't prepend /api/v1), "/spine/..." (spine API lives
  // under /api/spine, not /api/v1), and v1-relative. A leading "/v1/" is
  // stripped — API_BASE already ends in /v1, and call sites that include it
  // used to produce /api/v1/v1/* 404s.
  let p = path;
  if (!p.startsWith("http") && !p.startsWith("/api/")) {
    if (p.startsWith("/v1/")) p = p.slice(3);
    if (p.startsWith("/spine/")) p = `/api${p}`;
  }
  const url = p.startsWith("http")
    ? p
    : p.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL || ""}${p}`
      : `${API_BASE}${p}`;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  // FormData must go through untouched with no Content-Type — the browser
  // sets the multipart boundary itself (a manual header breaks the upload).
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const fetchOpts = {
    method: options.method || "GET",
    headers,
    cache: "no-store",
    signal: options.signal,
  };
  if (options.body && fetchOpts.method !== "GET") {
    fetchOpts.body = isFormData || typeof options.body === "string"
      ? options.body
      : JSON.stringify(options.body);
  }

  try {
    const resp = await fetch(url, fetchOpts);
    const text = await resp.text();
    let data;
    try { data = JSON.parse(text); } catch { data = {}; }

    if (resp.status === 401 && token) {
      clearSession();
      if (!window.location.pathname.startsWith("/login")) {
        const dest = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(dest)}`;
      }
      return { _error: true, status: 401, message: "Session expired. Please sign in again." };
    }

    if (!resp.ok) {
      return {
        _error: true,
        status: resp.status,
        message: data.detail || data.message || data.error || `Request failed (${resp.status})`,
      };
    }
    return data;
  } catch (err) {
    if (err.name === "AbortError") return { _error: true, status: 0, message: "Request cancelled" };
    // Handle rrweb recorder clone error by falling back to XMLHttpRequest
    if (err.message && err.message.includes("clone")) {
      try {
        const xhrData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(fetchOpts.method, url, true);
          Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
          xhr.onload = () => {
            let parsed;
            try { parsed = JSON.parse(xhr.responseText); } catch { parsed = {}; }
            if (xhr.status >= 200 && xhr.status < 300) resolve(parsed);
            else resolve({ _error: true, status: xhr.status, message: parsed.detail || parsed.message || `Request failed (${xhr.status})` });
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(fetchOpts.body || null);
        });
        return xhrData;
      } catch { /* fall through to generic error */ }
    }
    return {
      _error: true,
      status: 0,
      message: err.message === "Failed to fetch"
        ? "Network error — cannot reach API server"
        : err.message,
    };
  }
}

// ═══════════════════════════════════════════════════════════════
// REACT HOOK: useFetch — declarative data loading
// ═══════════════════════════════════════════════════════════════
export function useFetch(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!path) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    const result = await apiFetch(path, { signal: controller.signal });
    if (controller.signal.aborted) return;
    if (result?._error) {
      setError(result);
      setData(null);
    } else {
      setData(result);
      setError(null);
    }
    setLoading(false);
  }, [path]);

  useEffect(() => {
    fetchData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
}

// ═══════════════════════════════════════════════════════════════
// REACT HOOK: useMutation — POST/PUT/DELETE with loading states
// ═══════════════════════════════════════════════════════════════
export function useMutation(path, method = "POST") {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (body) => {
    setLoading(true);
    setError(null);
    const result = await apiFetch(path, { method, body });
    if (result?._error) { setError(result); setData(null); }
    else { setData(result); setError(null); }
    setLoading(false);
    return result;
  }, [path, method]);

  return { mutate, loading, error, data };
}

// ═══════════════════════════════════════════════════════════════
// WEBSOCKET: Real-time graph cascade events
// ═══════════════════════════════════════════════════════════════
export function useGraphWS() {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  useEffect(() => {
    const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProto}//${window.location.host}/ws/graph`;
    let ws;
    let timer;

    function connect() {
      try {
        ws = new WebSocket(wsUrl);
        ws.onopen = () => setConnected(true);
        ws.onmessage = (evt) => {
          try { setLastEvent(JSON.parse(evt.data)); } catch {}
        };
        ws.onclose = () => { setConnected(false); timer = setTimeout(connect, 3000); };
        ws.onerror = () => ws.close();
      } catch {}
    }

    connect();
    return () => { if (ws) ws.close(); if (timer) clearTimeout(timer); };
  }, []);

  return { connected, lastEvent };
}

// ═══════════════════════════════════════════════════════════════
// NAVIGATION HELPER
// ═══════════════════════════════════════════════════════════════
export function useModuleNav() {
  let navigate;
  try { navigate = useNavigate(); } catch { navigate = null; }

  const go = useCallback((path) => {
    if (navigate) navigate(path);
    else window.location.href = path;
  }, [navigate]);

  return {
    toGraphTab: (tab) => go(`/graph-intelligence?tab=${tab}`),
    toSFDR: (view) => go(`/frameworks/sfdr?view=${view || "dashboard"}`),
    toMateriality: (state) => go(`/materiality-intelligence`),
    toEvidence: (id) => go(`/evidence/library`),
    toVoluntaryFW: (fw) => go(`/frameworks/voluntary`),
    toAuditTrail: () => go(`/audit/trail`),
  };
}

// ═══════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════
const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30",
  border: "#1a2744", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981",
  red: "#ef4444", cyan: "#06b6d4", purple: "#8b5cf6",
  teal: "#14b8a6", orange: "#f97316",
};

export function LoadingState({ message = "Loading..." }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ display: "inline-block", width: 24, height: 24, border: `2px solid ${C.border}`, borderTopColor: C.cyan, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{message}</div>
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  const msg = error?.message || "An error occurred";
  return (
    <div style={{ padding: "20px", borderRadius: 8, background: C.red + "08", border: `1px solid ${C.red}22`, textAlign: "center" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.red, marginBottom: 4 }}>Error</div>
      <div style={{ fontSize: 10, color: C.text, marginBottom: 8 }}>{msg}</div>
      {onRetry && (
        <button onClick={onRetry} style={{ padding: "4px 14px", borderRadius: 5, border: `1px solid ${C.cyan}44`, background: C.cyan + "12", color: C.cyan, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Retry</button>
      )}
    </div>
  );
}

export function EmptyState({ icon = "", title, subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: C.dim }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{title || "No data"}</div>
      {subtitle && <div style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

export function WSIndicator({ connected }) {
  return (
    <span data-testid="ws-indicator" style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "1px 7px", borderRadius: 99, fontSize: 8, fontWeight: 600,
      background: connected ? C.emerald + "12" : C.red + "12",
      color: connected ? C.emerald : C.red,
      border: `1px solid ${connected ? C.emerald : C.red}22`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
      {connected ? "Live" : "Offline"}
    </span>
  );
}
